/* Atomic local snapshots and persistent outbox, isolated from index.html. */
(() => {
  const LEGACY = 'renax-drums-songs-v1',
    PREFIX = 'renax-index2-store-v1:',
    SESSION = 'renax-index2-scope';
  let scope = 'guest',
    healthy = true,
    readOnly = false;
  /** @type {import('./types/library').SyncState | null} */ let state = null;
  /** @type {Pick<typeof import('./sync-core.js'), 'stage' | 'blankState'> | null} */ let core =
    null;
  /** @type {string | null} */ let lastRaw = null;
  try {
    scope = sessionStorage.getItem(SESSION) || 'guest';
  } catch {}
  if (!/^(guest|user:[A-Za-z0-9_-]+)$/.test(scope)) scope = 'guest';
  /** @template T @param {T} v @returns {T} */
  const clone = (v) => JSON.parse(JSON.stringify(v));
  function notify() {
    window.dispatchEvent(new CustomEvent('renax-storage', { detail: { scope, healthy } }));
  }
  /** @param {import('./types/library').SyncState} next */
  function save(next) {
    if (readOnly)
      throw Error('Source locale illisible : sauvegarde automatique suspendue pour la préserver.');
    if (localStorage.getItem(PREFIX + scope) !== lastRaw)
      throw Error(
        'Un autre onglet a modifié cette bibliothèque. Exportez votre copie puis rechargez.',
      );
    const raw = JSON.stringify(next);
    localStorage.setItem(PREFIX + scope, raw);
    if (localStorage.getItem(PREFIX + scope) !== raw)
      throw Error('La copie locale ne peut pas être vérifiée.');
    lastRaw = raw;
    state = next;
    healthy = true;
    notify();
  }
  function read() {
    try {
      const raw = localStorage.getItem(PREFIX + scope);
      lastRaw = raw;
      if (raw) {
        state = /** @type {import('./types/library').SyncState} */ (JSON.parse(raw));
        if (state.version !== 1 || !state.library)
          throw Error('Format de sauvegarde local incorrect.');
        return JSON.stringify(state.library);
      }
      // Preserve the old source, even after the verified copy has been made.
      return scope === 'guest' ? localStorage.getItem(LEGACY) : null;
    } catch (e) {
      healthy = false;
      readOnly = true;
      notify();
      throw e;
    }
  }
  const ready = import("./sync-core.js?v=23e45035d325").then((module) => {
    core = module;
  });
  window.RENAX_STORAGE = {
    ready,
    read,
    /** @param {import('./types/library').Library} library */
    write(library) {
      try {
        /** @type {import('./types/library').SyncState} */ const initial = state || {
          version: 1,
          library: clone(library),
          base: {},
          pending: {},
          conflicts: {},
          importDecisions: {},
        };
        const next = core
          ? core.stage(initial, library, () => crypto.randomUUID())
          : { ...initial, library: clone(library) };
        save(next);
        return true;
      } catch (e) {
        healthy = false;
        notify();
        throw e;
      }
    },
    /** @param {import('./types/library').Library} library */
    matches(library) {
      return !!state && JSON.stringify(state.library) === JSON.stringify(library);
    },
    get rawToken() {
      return lastRaw;
    },
    get readOnly() {
      return readOnly;
    },
    get scope() {
      return scope;
    },
    get healthy() {
      return healthy;
    },
    get state() {
      return clone(state);
    },
    /** @param {import('./types/library').SyncState} next */
    update(next) {
      save(clone(next));
    },
    /** @param {string} nextScope @param {import('./types/library').Library} empty */
    async switchScope(nextScope, empty) {
      if (window.RENAX_SONGS?.flushPending?.() === false)
        throw Error('Sauvegarde en attente : conservez votre copie avant de changer de compte.');
      await ready;
      if (!/^(guest|user:[A-Za-z0-9_-]+)$/.test(nextScope))
        throw Error('Identité de stockage incorrecte.');
      const preserveNavigation = scope === nextScope;
      sessionStorage.setItem(SESSION, nextScope);
      scope = nextScope;
      state = null;
      readOnly = false;
      lastRaw = null;
      const raw = read();
      const library = raw ? JSON.parse(raw) : clone(empty);
      try {
        RENAX_SONGS.validateLibrary(library);
      } catch (e) {
        readOnly = true;
        healthy = false;
        throw e;
      }
      if (!state) save(/** @type {NonNullable<typeof core>} */ (core).blankState(library));
      RENAX_SONGS.replaceLibrary(library, preserveNavigation);
      window.dispatchEvent(new Event('renax-navigation-restored'));
      notify();
    },
    guestState() {
      const raw = localStorage.getItem(PREFIX + 'guest');
      return raw ? JSON.parse(raw) : null;
    },
    guestLibrary() {
      const raw = localStorage.getItem(PREFIX + 'guest');
      return raw ? JSON.parse(raw).library : JSON.parse(localStorage.getItem(LEGACY) || 'null');
    },
    reconcile() {
      RENAX_SONGS.replaceLibrary(/** @type {NonNullable<typeof state>} */ (state).library, true);
    },
    markCorrupt() {
      readOnly = true;
      healthy = false;
      notify();
    },
    recoverSource() {
      return localStorage.getItem(PREFIX + scope) || localStorage.getItem(LEGACY);
    },
  };
  // Each tab keeps its own account scope; Firebase auth uses session persistence as well.
  window.addEventListener('storage', (e) => {
    if (e.key !== PREFIX + scope || !e.newValue) return;
    window.dispatchEvent(new CustomEvent('renax-other-tab'));
  });
})();
